import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { API_BASE } from './config';
import { createGuardPayment, subscribeGuardMembership } from './guard/guardMembershipApi';

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, refreshUser, logout } = useAuth();

  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);
  const [selectedPoints, setSelectedPoints] = useState(0);
  const [selectedPrice, setSelectedPrice] = useState(0);
  const [activeTab, setActiveTab] = useState('profile');
  const [ledger, setLedger] = useState<any[]>([]);
  const [ledgerPage, setLedgerPage] = useState(1);
  const [buying, setBuying] = useState(false);
  const [payHint, setPayHint] = useState<string | null>(null);
  const [guardBuying, setGuardBuying] = useState(false);
  const [guardHint, setGuardHint] = useState<string | null>(null);
  const guardCardRef = useRef<HTMLDivElement | null>(null);

  const LEDGER_PAGE_SIZE = 7;
  const totalLedgerPages = Math.max(1, Math.ceil(ledger.length / LEDGER_PAGE_SIZE));
  const ledgerPageSafe = Math.min(Math.max(1, ledgerPage), totalLedgerPages);
  const pagedLedger = useMemo(() => {
    const start = (ledgerPageSafe - 1) * LEDGER_PAGE_SIZE;
    return ledger.slice(start, start + LEDGER_PAGE_SIZE);
  }, [ledger, ledgerPageSafe]);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/login');
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/points/ledger`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setLedger(Array.isArray(data) ? data : []);
          setLedgerPage(1);
        }
      } catch (e) {
        console.warn('fetch ledger failed', e);
      }
    })();
  }, [navigate]);

  useEffect(() => {
    if (location.hash === '#guard-membership' && activeTab === 'profile') {
      const timer = window.setTimeout(() => {
        guardCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [activeTab, location.hash]);

  useEffect(() => {
    if (user?.guard_has_access) {
      setGuardHint(null);
    }
  }, [user?.guard_has_access]);

  // 已移除“失败补回”用户入口：退款仅由系统或管理员处理。

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const openModal = (points: number, price: number) => {
    setSelectedPoints(points);
    setSelectedPrice(price);
    setPurchaseModalOpen(true);
  };

  const closeModal = () => {
    setPurchaseModalOpen(false);
    setSelectedPoints(0);
    setSelectedPrice(0);
  };

  const skuFor = (points: number) => {
    if (points === 30) return 'points_30';
    if (points === 100) return 'points_100';
    if (points === 500) return 'points_500';
    if (points === 1000) return 'points_1000';
    return null;
  };

  const handleBuy = async () => {
    if (buying) return;
    const sku = skuFor(selectedPoints);
    if (!sku) return;

    const token = localStorage.getItem('access_token');
    if (!token) {
      setPayHint('登录已过期，请重新登录');
      return;
    }

    setBuying(true);
    setPayHint(null);

    try {
      const res = await fetch(`${API_BASE}/api/pay/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          sku,
          pay_type: 'alipay'
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.detail || '创建支付失败');
      }

      if (!data?.pay_url) {
        throw new Error('创建支付失败：未返回支付链接');
      }

      // 跳转到支付页面
      window.location.href = data.pay_url;

    } catch (e: any) {
      setPayHint(e?.message || '创建支付失败');
      setBuying(false);
    }
  };

  const guardExpiryLabel = useMemo(() => {
    const value = user?.guard_access_until;
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, [user?.guard_access_until]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-blue-50">
        <div className="text-xl font-semibold text-blue-600">加载中...</div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const pointsOptions = [
    { points: 30, price: 3 },
    { points: 100, price: 10 },
    { points: 500, price: 50 },
    { points: 1000, price: 100 }
  ];
  const levelProgress = (user.points % 500) / 500 * 100;

  const roleLabel: Record<string, string> = {
    free: '免费用户',
    creator: '创作者',
    business: '企业用户'
  };

  const handleGuardBuy = async () => {
    if (guardBuying || user?.guard_has_access) return;

    const token = localStorage.getItem('access_token');
    if (!token) {
      setGuardHint('登录已过期，请重新登录');
      return;
    }

    setGuardBuying(true);
    setGuardHint(null);

    try {
      if ((user?.points || 0) >= 10) {
        await subscribeGuardMembership(token);
        await refreshUser();
        setGuardHint(null);
        return;
      }

      const payment = await createGuardPayment(token);
      if (!payment?.pay_url) {
        throw new Error('创建守卫月卡支付失败');
      }
      window.location.assign(payment.pay_url);
    } catch (e: any) {
      setGuardHint(e?.message || '守卫月卡开通失败');
    } finally {
      setGuardBuying(false);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-[#eff6ff]">
      {/* 侧边栏 */}
      <aside className="sidebar flex flex-col items-center py-4">
        <div className="sidebar-item" onClick={() => navigate('/home')}>
          <div className="icon-wrapper">
            <div className="hover-bg"></div>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
          </div>
        </div>

        <div className="w-full flex-1 flex flex-col justify-center gap-4">
          <div className={`sidebar-item ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => setActiveTab('profile')}>
            <div className="shape-bg">
              <svg className="svg-shape" viewBox="0 0 54 72" preserveAspectRatio="none">
                <path d="M 54,0 Q 13.5,0 13.5,18 L 13.5,54 Q 13.5,72 54,72 L 54,0 Z" />
              </svg>
            </div>
            <div className="bottom-curve"></div>
            <div className="icon-wrapper">
              <div className="hover-bg"></div>
              {activeTab === 'profile' ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2a5 5 0 1 0 5 5 5 5 0 0 0-5-5zm0 12a8 8 0 0 0-8 8h16a8 8 0 0 0-8-8z" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="8" r="5" />
                  <path d="M20 21a8 8 0 1 0-16 0" />
                </svg>
              )}
            </div>
          </div>

          <div className={`sidebar-item ${activeTab === 'usage' ? 'active' : ''}`} onClick={() => setActiveTab('usage')}>
            <div className="shape-bg">
              <svg className="svg-shape" viewBox="0 0 54 72" preserveAspectRatio="none">
                <path d="M 54,0 Q 13.5,0 13.5,18 L 13.5,54 Q 13.5,72 54,72 L 54,0 Z" />
              </svg>
            </div>
            <div className="bottom-curve"></div>
            <div className="icon-wrapper">
              <div className="hover-bg"></div>
              {activeTab === 'usage' ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 3v18h18v-2H5V3H3zm5 14h2v-3H8v3zm5 0h2V5h-2v12zm5 0h2V9h-2v8z" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 3v18h18" />
                  <path d="M18 17V9" />
                  <path d="M13 17V5" />
                  <path d="M8 17v-3" />
                </svg>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* 主体内容 */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {activeTab === 'profile' && (
          <header className="pt-16 pb-12 px-8 border-b border-blue-100 bg-white">
            <div className="max-w-7xl mx-auto w-full flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-blue-50 border-2 border-blue-500 flex items-center justify-center text-blue-600 shadow-sm">
                    <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
                  </div>
                  <div className="flex flex-col items-start">
                    <div className="flex items-center gap-2">
                      <h1 className="text-2xl font-bold text-slate-800">{user.username}</h1>
                        <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs rounded-full font-bold">
                          {roleLabel[user.role] || user.role || 'free'}
                        </span>
                    </div>
                    <p className="text-slate-400 text-sm mt-1">{user.email}</p>
                    
                    <div className="mt-5">
                      <div className="text-slate-400 text-[10px] font-bold uppercase tracking-[0.2em] mb-1">剩余积分</div>
                      <div className="text-4xl font-black text-blue-600 leading-none">{user.points || 0}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </header>
        )}

        <section className={`flex-1 overflow-y-auto p-8 ${activeTab === 'usage' ? 'flex items-center' : ''}`}>
          <div className="max-w-7xl mx-auto w-full">
            {activeTab === 'profile' ? (
              <div className="flex flex-col gap-6">
                <div
                  data-testid="points-purchase-grid"
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                >
                  {pointsOptions.map((option, index) => (
                    <div key={index} className="bg-white rounded-2xl p-4 card-shadow border border-blue-50 flex flex-col items-center justify-between hover:border-blue-200 transition-all cursor-pointer group hover:-translate-y-1">
                      <div className="aspect-square bg-blue-50/50 rounded-xl mb-4 flex flex-col items-center justify-center group-hover:bg-blue-100 transition-colors w-full relative overflow-hidden">
                        <span className="points-text text-4xl font-black tracking-tighter">{option.points}</span>
                        <span className="text-[10px] text-blue-400 font-bold tracking-widest uppercase mt-1">POINTS</span>

                        <svg className="absolute -right-2 -bottom-2 w-12 h-12 text-blue-500/10 rotate-12" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z"/>
                        </svg>
                      </div>
                      <button
                        onClick={() => openModal(option.points, option.price)}
                        className="w-full py-2 bg-black rounded-lg text-white font-semibold text-sm hover:bg-zinc-800 transition-colors"
                      >
                        购买积分
                      </button>
                    </div>
                  ))}
                </div>
                <div
                  data-testid="guard-membership-row"
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                >
                  <div
                    id="guard-membership"
                    ref={guardCardRef}
                    className="bg-white rounded-2xl p-4 card-shadow border border-blue-50 flex flex-col items-center justify-between hover:border-blue-200 transition-all group"
                  >
                    <div className="aspect-square bg-blue-50/60 rounded-xl mb-4 flex flex-col items-center justify-center w-full relative overflow-hidden">
                      <span className="points-text text-3xl font-black tracking-tight text-slate-800">守卫月卡</span>
                      <span className="text-[10px] text-blue-400 font-bold tracking-widest uppercase mt-2">10 积分</span>
                      <span className="text-xs text-slate-400 mt-2">30 天</span>
                      <svg className="absolute -right-2 -bottom-2 w-12 h-12 text-blue-500/10 rotate-12" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z"/>
                      </svg>
                    </div>
                    <div
                      className={`w-full mb-3 text-center text-xs min-h-[32px] flex items-center justify-center px-2 ${
                        user?.guard_has_access && guardExpiryLabel ? 'text-emerald-600 font-semibold' : 'text-slate-500'
                      }`}
                    >
                      {user?.guard_has_access && guardExpiryLabel
                        ? `已开通，有效期至 ${guardExpiryLabel}`
                        : '积分不足时可直接支付 1 元开通'}
                    </div>
                    {guardHint && !user?.guard_has_access && (
                      <div className="w-full mb-3 text-center text-xs text-blue-600 font-semibold">{guardHint}</div>
                    )}
                    <button
                      onClick={handleGuardBuy}
                      disabled={guardBuying || Boolean(user?.guard_has_access)}
                      className="w-full py-2 bg-black rounded-lg text-white font-semibold text-sm hover:bg-zinc-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {user?.guard_has_access ? '已开通' : guardBuying ? '处理中...' : '立即开通'}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-3xl shadow-sm border border-blue-100 overflow-hidden">
                <div className="p-6 border-b border-blue-50 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <h2 className="text-xl font-bold text-slate-800">积分流水</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50/50">
                        <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Model</th>
                        <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Reason</th>
                        <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Time</th>
                        <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Task</th>
                        <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase tracking-wider text-right">Delta</th>
                        <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase tracking-wider text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {ledger.length > 0 ? (
                        pagedLedger.map((it, idx) => (
                          <tr key={it?.id ?? `${ledgerPageSafe}-${idx}`} className="hover:bg-blue-50/30 transition-colors">
                            <td className="px-6 py-4"><span className="text-sm font-bold text-slate-700 font-mono">{it.model || '-'}</span></td>
                            <td className="px-6 py-4 text-sm text-slate-500">{it.reason || it.category}</td>
                            <td className="px-6 py-4 text-sm text-slate-500">{it.created_at ? new Date(it.created_at).toLocaleString() : '-'}</td>
                            <td className="px-6 py-4 text-sm text-slate-500 font-mono">{it.external_id || '-'}</td>
                            <td className="px-6 py-4 text-right">
                              <span className={`text-sm font-black ${Number(it.delta) < 0 ? 'text-slate-900' : 'text-emerald-600'}`}>
                                {Number(it.delta) > 0 ? `+${it.delta}` : it.delta}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-slate-50 text-slate-600 border border-slate-200">
                                {it.status || 'committed'}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={6} className="px-6 py-12">
                            <div className="flex flex-col items-center justify-center text-slate-500">
                              <div className="w-16 h-16 mb-3 rounded-full bg-slate-100 flex items-center justify-center mx-auto">
                                <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7h18M5 11h14M7 15h10" />
                                </svg>
                              </div>
                              <div className="text-sm text-center">还未产生历史记录</div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* 分页（前端本地分页） */}
                {ledger.length > LEDGER_PAGE_SIZE && (
                  <div className="px-6 py-4 border-t border-blue-50 flex items-center justify-between">
                    <div className="text-xs text-slate-500">
                      共 {ledger.length} 条 · 第 {ledgerPageSafe} / {totalLedgerPages} 页
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        className="px-3 py-1.5 rounded-lg text-xs font-bold bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={ledgerPageSafe <= 1}
                        onClick={() => setLedgerPage((p) => Math.max(1, p - 1))}
                      >
                        上一页
                      </button>
                      <button
                        className="px-3 py-1.5 rounded-lg text-xs font-bold bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={ledgerPageSafe >= totalLedgerPages}
                        onClick={() => setLedgerPage((p) => Math.min(totalLedgerPages, p + 1))}
                      >
                        下一页
                      </button>
                    </div>
                  </div>
                )}
                {/* 失败补回入口已移除：不再对用户展示 */}
              </div>
            )}
          </div>
        </section>
      </main>

      {/* 弹窗部分 */}
      {purchaseModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4" onClick={closeModal}>
          <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-4">购买积分</h2>
            <p className="text-slate-500 mb-2">
              您正在购买 <span className="font-bold text-black">{selectedPoints}</span> 积分
            </p>
            <p className="text-slate-500 mb-6">
              支付金额：<span className="font-bold text-blue-600 text-lg">￥{selectedPrice}</span>
            </p>
            {payHint && (
              <p className="text-red-500 text-sm mb-4">{payHint}</p>
            )}
            <div className="flex gap-4">
              <button
                onClick={handleBuy}
                disabled={buying}
                className="flex-1 py-2 bg-black text-white rounded-lg hover:bg-zinc-800 transition-colors disabled:opacity-50"
              >
                {buying ? '处理中...' : '立即支付'}
              </button>
              <button
                onClick={closeModal}
                disabled={buying}
                className="flex-1 py-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
